<template>
    <div class="outfit-card-background" :class="containerClasses">
        <article class="outfit-card" :class="cardClasses">
            <div
                v-for="(product, index) in getProductsOrderByPriority"
                :key="product.product_id"
                class="outfit-card-item"
                :class="itemClasses[index]"
                @click.stop="() => handleProductClk(product, outfit)">
                    <swap-item-button classes="icon" iconSize="21px" @click="() => swapProductClk(product)" v-if="swapProductClk !== undefined" />
                    <div class="outfit-card-item-image-container">
                        <img class="outfit-card-item-image" ref="image" @load="loadImage(product.product_id)" :src="product.product_image_url" :id="product.product_id">
                    </div>
            </div>
        </article>
        <slot name="footer-buttons" />
    </div>
</template>

<script>
    import Mixins from '../../mixins/mixins';
    import Loader from '../common/loader.vue';
    import SwapItemButton from '../products/swap-item-button.vue';
    import { eventNames } from '../../store/utils/tracking';

    export default {
        mixins: [Mixins],
        components: { Loader, SwapItemButton },
        props: {
            outfit: Object,
            handleProductClk: Function,
            swapProductClk: Function,
        },
        data() {
            return {
                imageLoaded: [],
            };
        },
        computed: {
            getProductsOrderByPriority() {
                const { products } = this.outfit;

                if(!products) return [];

                const productShoe = products.find(product => this.getPriorityIndex(product) == 4);
                if(productShoe) {
                    const productsWithoutShoe = products.filter(product => this.getPriorityIndex(product) != 4);
                    return [
                        ...this.orderByPriority(productsWithoutShoe, false),
                        { 
                            ...productShoe,
                            outfit_priority: 4,
                        }
                    ];
                }
                
                return this.orderByPriority(products);
            },
            itemClasses() {
                return this.getProductsOrderByPriority.map((product, index) => {
                    return this.getItemClasses(product, index);
                });
            },
            containerClasses() {
                return {
                    'outfit-card-background--blank': this.getProductsOrderByPriority.length === 0,
                    'outfit-card-background--with-footer-buttons': !!this.$slots['footer-buttons'],
                };
            },
            cardClasses() {
                return {
                    [`outfit-card--${this.getProductsOrderByPriority.length}-products`]: true,
                    'outfit-card--no-shoe': !this.hasShoe,
                }
            },
            hasShoe() {
                return this.getProductsOrderByPriority.filter(product => product.outfit_priority == 4).length > 0;
            },
            completeLoaded() {
                return this.imageLoaded.length > 0 && this.imageLoaded.every(imageStatus => imageStatus.loaded);
            },
        },
        methods: {
            getItemClasses(product, index) {
                const priorityIndex = product.outfit_priority;
                const itemClass = `outfit-card-priority-${priorityIndex}`;
                
                return {
                    [itemClass]: true,
                    [`${itemClass}--extended`]: this.itemShouldExtend(priorityIndex),
                };
            },
            getPriorityIndex(product) {
                return product.outfit_priority || product.outfit_priority_parent || 6;
            },
            orderByPriority(products, useShoePlace = true) {
                return products
                    .map(product => ({
                        ...product,
                        outfit_priority: new Number(this.getPriorityIndex(product)),
                    }))
                    .sort((a, b) => {
                        return a.outfit_priority - b.outfit_priority;
                    }).map((product, index) => ({
                        ...product,
                        outfit_priority: index + ((index === 3 && !useShoePlace) ? 2 : 1),    
                    }));
            },
            itemShouldExtend(index) {
                switch(index) {
                    case 2:
                        return this.getProductsOrderByPriority.length < 4 && this.hasShoe;
                    case 3:
                    case 1:
                        return this.getProductsOrderByPriority.length < 4 && !this.hasShoe;
                    case 4:
                        return this.getProductsOrderByPriority.length < 5;
                    default:
                        return false;
                }
            },
            loadImage(id) {
                const index = this.imageLoaded.findIndex(imageStatus => imageStatus.id == id);
                this.imageLoaded.splice(index, 1, {
                    id,
                    loaded: true,
                });
            },
            setImagesLoaded() {
                this.$nextTick(() => {
                    if (typeof this.$refs.image !== 'undefined') {
                        this.imageLoaded = this.$refs.image.map(image => {
                            return {
                                id: image.id,
                                loaded: image.complete,
                            };
                        });
                    }
                });
            },
            trackOutfitViewEvent() {
                this.$helpers.trackEvent(eventNames.VIEW_OUTFIT, {content_id: this.outfit.outfit_id, price: this.outfit.outfit_price});
            }
        },
        mounted() {
            this.setImagesLoaded();
        },
        watch: {
            'outfit.outfit_id'() {
                this.setImagesLoaded();
                this.trackOutfitViewEvent();
            }
        },
    }
</script>

<style lang="scss" scoped>
    .outfit-card-background {
        border: 1px solid #F0F0F0;
        border-radius: 20px;
        box-shadow: 2px 2px 8px 0 rgba(0,0,0,0.04);
        background-color: #EEEEEE;
        height: 100%;
        flex-direction: column;
        &--blank {
            background: none;
        }
        &--with-footer-buttons {
            .outfit-card {
                height: calc(100% - 86px);
            }
            .outfit-card-priority {
                &-4, &-5, &-1--extended, &-3--extended {
                    border-radius: 0;
                }
                &-1, &-3 {
                    border-bottom-right-radius: 0;
                    border-bottom-left-radius: 0;
                }
            }
        }
    }
    .outfit-card {
        $card: &;
        display: grid;
        grid-template-columns: 0.57fr 0.43fr;
        grid-template-rows: 0.37fr 0.37fr 0.26fr;
        grid-template-areas: "priority-1 priority-2"
            "priority-1 priority-3"
            "priority-4 priority-5";
        height: 100%;
        grid-gap: 1px 1px;
        &--3-products {
            grid-template-areas: "priority-1 priority-2"
                "priority-1 priority-2"
                "priority-4 priority-4";
            &#{$card}--no-shoe{
                grid-template-rows: 0.5fr 0.5fr;
                grid-template-areas: "priority-1 priority-2"
                    "priority-1 priority-3"
            }
        }
        &--4-products {
            grid-template-areas: "priority-1 priority-2"
                "priority-1 priority-3"
                "priority-4 priority-4";
        }
    }
    .outfit-card-item{
        background-color: #FFFFFF;
        overflow: hidden;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 15px 30px;
        .icon {
            position: absolute;
            right: 10px;
            top: 10px;
            z-index: 1;
        }
        .outfit-card-item-image-container {
            height: 100%;
            .outfit-card-item-image {
                display: block;
                height: 100%; 
                width: 100%;
                object-fit: contain;
            }        
        }
        
    }
    .outfit-card-priority-1 {
        grid-area: priority-1;
        border-radius: 20px 0 0;
        &--extended {
            border-radius: 20px 0 0 20px;
        }
    }
    .outfit-card-priority-2 {
        grid-area: priority-2;
        border-radius: 0 20px 0 0;
    }
    .outfit-card-priority-3 {
        grid-area: priority-3;
        &--extended {
            border-radius: 0 0 20px;
        }
    }
    .outfit-card-priority-4 {
        grid-area: priority-4;
        border-radius: 0 0 0 20px;
        padding: 5px 20px 5px 20px;
        &--extended {
            border-radius: 0 0 20px 20px;
        }
        .outfit-card-item-image {
            margin: 0 auto;
        }
    }
    .outfit-card-priority-5 {
        grid-area: priority-5;
        border-radius: 0 0 20px;
        padding: 5px 20px 5px 20px;
    }
</style>